import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const OwnerManagement = ({ asset, isEditing, onUpdate }) => {
  const [showChangeOwner, setShowChangeOwner] = useState(false);
  const [newOwner, setNewOwner] = useState({
    name: '',
    email: '',
    department: '',
    employeeId: '',
    notifyOwner: true
  });

  const departmentOptions = [
    { value: 'it', label: 'Information Technology' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' },
    { value: 'operations', label: 'Operations' },
    { value: 'legal', label: 'Legal' },
    { value: 'executive', label: 'Executive' }
  ];

  const employeeOptions = [
    { value: 'arjun.sharma@company.com', label: 'Arjun Sharma - IT Administrator', department: 'it', employeeId: 'EMP001' },
    { value: 'priya.patel@company.com', label: 'Priya Patel - HR Manager', department: 'hr', employeeId: 'EMP002' },
    { value: 'rahul.verma@company.com', label: 'Rahul Verma - Finance Analyst', department: 'finance', employeeId: 'EMP003' },
    { value: 'neha.gupta@company.com', label: 'Neha Gupta - Marketing Specialist', department: 'marketing', employeeId: 'EMP004' },
    { value: 'vikram.nair@company.com', label: 'Vikram Nair - Sales Manager', department: 'sales', employeeId: 'EMP005' },
    { value: 'anjali.singh@company.com', label: 'Anjali Singh - Operations Coordinator', department: 'operations', employeeId: 'EMP006' },
    { value: 'suresh.reddy@company.com', label: 'Suresh Reddy - Legal Counsel', department: 'legal', employeeId: 'EMP007' },
    { value: 'kavita.joshi@company.com', label: 'Kavita Joshi - Executive Assistant', department: 'executive', employeeId: 'EMP008' }
  ];

  const ownershipHistory = asset?.ownershipHistory || [
    {
      id: 1,
      ownerName: 'Arjun Sharma',
      ownerEmail: 'arjun.sharma@company.com',
      department: 'Information Technology',
      employeeId: 'EMP001',
      assignedDate: '2025-08-01T09:00:00Z',
      assignedBy: 'System Administrator',
      reason: 'Initial assignment',
      isCurrent: true
    },
    {
      id: 2,
      ownerName: 'Priya Patel',
      ownerEmail: 'priya.patel@company.com',
      department: 'Human Resources',
      employeeId: 'EMP002',
      assignedDate: '2025-07-15T14:30:00Z',
      assignedBy: 'IT Manager',
      reason: 'Temporary assignment during IT staff transition',
      unassignedDate: '2025-08-01T09:00:00Z',
      isCurrent: false
    }
  ];

  const currentOwner = ownershipHistory?.find(owner => owner?.isCurrent) || ownershipHistory?.[0];

  const handleOwnerChange = (email) => {
    const selectedEmployee = employeeOptions?.find(emp => emp?.value === email);
    if (selectedEmployee) {
      setNewOwner({
        name: selectedEmployee?.label?.split(' - ')?.[0],
        email: selectedEmployee?.value,
        department: selectedEmployee?.department,
        employeeId: selectedEmployee?.employeeId,
        notifyOwner: true
      });
    }
  };

  const handleAssignOwner = () => {
    if (newOwner?.email && newOwner?.name) {
      const newOwnerRecord = {
        id: Date.now(),
        ownerName: newOwner?.name,
        ownerEmail: newOwner?.email,
        department: departmentOptions?.find(dept => dept?.value === newOwner?.department)?.label || newOwner?.department,
        employeeId: newOwner?.employeeId,
        assignedDate: new Date()?.toISOString(),
        assignedBy: 'Current User',
        reason: 'Owner change via asset management',
        isCurrent: true
      };

      // Update previous owner record
      const updatedHistory = ownershipHistory?.map(owner => 
        owner?.isCurrent 
          ? { ...owner, isCurrent: false, unassignedDate: new Date()?.toISOString() }
          : owner
      );

      updatedHistory?.push(newOwnerRecord);
      onUpdate({ ...asset, ownershipHistory: updatedHistory });

      // Reset form
      setNewOwner({
        name: '',
        email: '',
        department: '',
        employeeId: '',
        notifyOwner: true
      });
      setShowChangeOwner(false);

      // Simulate notification
      if (newOwner?.notifyOwner) {
        console.log(`Notification sent to ${newOwner?.email} about asset assignment`);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Owner Management</h3>
        {isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowChangeOwner(!showChangeOwner)}
            iconName="UserCheck"
            iconPosition="left"
          >
            Change Owner
          </Button>
        )}
      </div>
      {/* Current Owner Information */}
      <div className="bg-muted rounded-lg p-4 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={20} color="white" />
            </div>
            <div>
              <h4 className="text-lg font-medium text-foreground">{currentOwner?.ownerName || 'Unassigned'}</h4>
              <p className="text-sm text-muted-foreground">{currentOwner?.ownerEmail || 'No email'}</p>
              <div className="flex items-center space-x-4 mt-2 text-sm">
                <span className="flex items-center space-x-1">
                  <Icon name="Building" size={14} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{currentOwner?.department || 'No department'}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Badge" size={14} className="text-muted-foreground" />
                  <span className="text-muted-foreground">{currentOwner?.employeeId || 'No ID'}</span>
                </span>
              </div>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="text-muted-foreground">Assigned</p>
            <p className="font-medium text-foreground">
              {currentOwner?.assignedDate ? formatDate(currentOwner?.assignedDate) : 'Unknown'}
            </p>
          </div>
        </div>
      </div>
      {/* Change Owner Form */}
      {showChangeOwner && isEditing && (
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-6">
          <h4 className="text-md font-medium text-foreground mb-4">Assign New Owner</h4>
          
          <div className="space-y-4">
            <Select
              label="Select Employee"
              options={employeeOptions}
              value={newOwner?.email}
              onChange={handleOwnerChange}
              searchable
              placeholder="Search and select employee"
              description="Start typing to search by name or email"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Employee Name"
                type="text"
                value={newOwner?.name}
                onChange={(e) => setNewOwner({ ...newOwner, name: e?.target?.value })}
                disabled
                placeholder="Auto-filled from selection"
              />

              <Input
                label="Employee ID"
                type="text"
                value={newOwner?.employeeId}
                onChange={(e) => setNewOwner({ ...newOwner, employeeId: e?.target?.value })}
                disabled
                placeholder="Auto-filled from selection"
              />
            </div>

            <Select
              label="Department"
              options={departmentOptions}
              value={newOwner?.department}
              onChange={(value) => setNewOwner({ ...newOwner, department: value })}
              disabled
              placeholder="Auto-filled from selection"
            />

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="notifyOwner"
                checked={newOwner?.notifyOwner}
                onChange={(e) => setNewOwner({ ...newOwner, notifyOwner: e?.target?.checked })}
                className="w-4 h-4 text-primary bg-input border-border rounded focus:ring-ring"
              />
              <label htmlFor="notifyOwner" className="text-sm text-foreground">
                Send email notification to new owner
              </label>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setShowChangeOwner(false)}>
                Cancel
              </Button>
              <Button 
                variant="default" 
                onClick={handleAssignOwner}
                disabled={!newOwner?.email || !newOwner?.name}
                iconName="UserCheck"
                iconPosition="left"
              >
                Assign Owner
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Ownership History */}
      <div>
        <h4 className="text-md font-medium text-foreground mb-4">Ownership History</h4>
        <div className="space-y-3">
          {ownershipHistory?.map((owner) => (
            <div key={owner?.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  owner?.isCurrent ? 'bg-success' : 'bg-muted'
                }`}>
                  <Icon 
                    name="User" 
                    size={14} 
                    color={owner?.isCurrent ? 'white' : 'currentColor'}
                    className={!owner?.isCurrent ? 'text-muted-foreground' : ''}
                  />
                </div>
                <div>
                  <p className="font-medium text-foreground">{owner?.ownerName}</p>
                  <p className="text-sm text-muted-foreground">{owner?.department}</p>
                </div>
              </div>
              
              <div className="text-right text-sm">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-muted-foreground">Assigned:</span>
                  <span className="text-foreground">{formatDate(owner?.assignedDate)}</span>
                </div>
                {owner?.unassignedDate && (
                  <div className="flex items-center space-x-2">
                    <span className="text-muted-foreground">Unassigned:</span>
                    <span className="text-foreground">{formatDate(owner?.unassignedDate)}</span>
                  </div>
                )}
                {owner?.isCurrent && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success text-success-foreground">
                    Current Owner
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" iconName="Mail" iconPosition="left">
            Contact Owner
          </Button>
          <Button variant="outline" size="sm" iconName="Calendar" iconPosition="left">
            Schedule Handover
          </Button>
          <Button variant="outline" size="sm" iconName="FileText" iconPosition="left">
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OwnerManagement;